import { useEffect } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function getFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/**
 * Comportement commun aux surfaces modales (modales + tiroir de navigation) :
 * fermeture à la touche Échap, piège à focus sur Tab / Shift+Tab, focus
 * initial dans le conteneur et restauration du focus à la fermeture.
 *
 * @param {object}   options
 * @param {boolean}  options.open
 * @param {Function} options.onClose        appelé sur Échap (ignoré si `dismissible` est faux)
 * @param {object}   options.containerRef   ref du conteneur qui piège le focus
 * @param {object}   [options.initialFocusRef] élément à focuser à l'ouverture
 * @param {boolean}  [options.dismissible]  false pendant un chargement
 * @param {boolean}  [options.trapFocus]    false pour ne gérer que Échap
 */
export default function useDismissable({
  open,
  onClose,
  containerRef,
  initialFocusRef = null,
  dismissible = true,
  trapFocus = true,
}) {
  // Focus initial + restauration du focus sortant.
  useEffect(() => {
    if (!open) return undefined;

    const previous =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // rAF : laisse React peindre le conteneur avant d'y chercher un focusable.
    const raf = requestAnimationFrame(() => {
      const target =
        initialFocusRef?.current ||
        getFocusable(containerRef.current)[0] ||
        containerRef.current;
      target?.focus?.({ preventScroll: true });
    });

    return () => {
      cancelAnimationFrame(raf);
      if (previous && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [open, containerRef, initialFocusRef]);

  // Échap + piège à focus.
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!dismissible) return;
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (!trapFocus || e.key !== 'Tab') return;

      const container = containerRef.current;
      const nodes = getFocusable(container);
      if (nodes.length === 0) {
        e.preventDefault();
        container?.focus({ preventScroll: true });
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (!container?.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, onClose, containerRef, dismissible, trapFocus]);
}
